import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../config/api";
import { useAuth } from "../context/AuthContext";

// Google OAuth landing page. Emergent's managed auth redirects here
// after a successful Google login with `#session_id=<one-time-token>`.
// We POST the session_id to /api/auth/google/session-exchange which
// returns the SAME shape as /api/auth/login.
//
// Edge cases handled:
//   - no session_id in URL → friendly error, route back to /
//   - duplicate visits to the same page → idempotent (server token is
//     one-time but a second call just fails 401, we suppress the toast)
//   - already signed in on this device → still works, the new JWT
//     overwrites the old one
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { setSessionFromQR } = useAuth();
  const [stage, setStage] = useState("exchanging"); // exchanging | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const fragment = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(fragment);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setStage("error");
      setErrorMsg("Sign-in didn't return a session. Please try again.");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session-exchange`, {
          session_id: sessionId,
        });
        setSessionFromQR(res.data); // same shape as login response
        setStage("done");
        toast.success("Signed in with Google.");
        // Clear the fragment so a refresh doesn't re-trigger.
        window.history.replaceState({}, "", "/auth/google/callback");
        setTimeout(() => navigate("/dashboard", { replace: true }), 600);
      } catch (err) {
        setStage("error");
        setErrorMsg(err?.response?.data?.detail || "Google sign-in failed.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black flex items-center justify-center px-4 py-12" data-testid="google-auth-callback">
      <div className="w-full max-w-md bg-gray-900/80 backdrop-blur border border-gray-800 rounded-2xl p-8 text-center">
        {stage === "exchanging" && (
          <>
            <Loader2 className="h-10 w-10 text-emerald-400 animate-spin mx-auto mb-3" />
            <h1 className="text-white text-lg font-semibold mb-1">Signing you in…</h1>
            <p className="text-gray-400 text-sm">One moment while we finalize your Google session.</p>
          </>
        )}
        {stage === "done" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
            <h1 className="text-white text-lg font-semibold mb-1">Welcome!</h1>
            <p className="text-gray-400 text-sm">Redirecting to your dashboard…</p>
          </>
        )}
        {stage === "error" && (
          <>
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <h1 className="text-white text-lg font-semibold mb-1">Couldn't finish sign-in</h1>
            <p className="text-gray-400 text-sm mb-5">{errorMsg}</p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-md transition-colors"
              data-testid="google-auth-error-back-btn"
            >
              Back to home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
